import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LuCalendarDays, LuMapPin } from 'react-icons/lu';
import { GoPerson } from 'react-icons/go';
import { HiOutlineShare } from 'react-icons/hi';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { SlOptionsVertical } from 'react-icons/sl';
import { PiPulseBold } from 'react-icons/pi';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Menu,
  Paper,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import DATE_FORMATS from '@constants/dateFormat';
import { IJobStats } from '@th-types/jobs.type';
import IJobDetail, { JobMember } from '@th-types/job.detail.type';
import { formatLocalDate, UTC_TIMEZONE } from '@utils/DateUtils';
import { BASE_PATH } from '@utils/Environment';
import { useUserContext } from '@state/userContext';
import TwoPersonIcon from '@components/Icons/TwoPerson/TwoPerson';
import NotificationBellIcon from '@components/Icons/NotificationBell/NotificationBell';
import ViewOffSlashIcon from '@components/Icons/ViewOffSlash/ViewOffSlash';
import ClipboardIcon from '@components/Icons/Clipboard/Clipboard';
import DiamondIcon from '@components/Icons/Diamond/Diamond';
import {
  getFilledPosition,
  getGoogleMapsUrl,
  getTotalApplicants,
  getTotalHiddenApplicants,
  isJobHidden,
  LARGE_ICONS_SIZE,
} from '@utils/JobUtils';
import { CandidateStage } from '@company/types/job-overview.type';

const MOBILE_STAT_ICON_SIZE = 18;
const MOBILE_META_ICON_SIZE = 14;

interface Props {
  job: IJobDetail;
  jobStats: IJobStats;
  hideJob: (job: IJobDetail) => void;
  showJobMembers: (members: JobMember[]) => void;
  handleOpenModalShareJobLink: (job: IJobDetail) => void;
}

interface MobileStatConfig {
  label: string;
  stage: CandidateStage;
  value: ReactNode;
  icon: ReactNode;
}

function MobileStatIndicator({
  label,
  value,
  icon,
  to,
  testId,
}: {
  label: string;
  value: ReactNode;
  icon: ReactNode;
  to: string;
  testId: string;
}) {
  return (
    <Popover position="top" withArrow shadow="md">
      <Popover.Target>
        <UnstyledButton
          data-testid={testId}
          aria-label={label}
          style={{ flex: 1, minWidth: 0 }}
        >
          <Stack align="center" gap={1} py={2}>
            {icon}
            <Text span fw={700} fz={11} lh={1.1}>
              {value}
            </Text>
          </Stack>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={8}>
        <Button
          component={Link}
          to={to}
          target="_blank"
          variant="light"
          size="compact-sm"
        >
          Go to {label}
        </Button>
      </Popover.Dropdown>
    </Popover>
  );
}

export default function JobTableItemMobile({
  job,
  jobStats,
  hideJob,
  showJobMembers,
  handleOpenModalShareJobLink,
}: Props) {
  const { id: companyMemberId } = useUserContext();

  const {
    id,
    position,
    eventName,
    brandRepresented,
    localizedStart,
    localizedEnd,
    datePublished,
    address,
    members,
    nationalJob,
  } = job;

  const jobDetailTabLink = (stage: CandidateStage) =>
    `${BASE_PATH}jobs/${id}?tab=${stage}`;

  const stats: MobileStatConfig[] = [
    {
      label: 'Hired',
      stage: CandidateStage.HIRED,
      value: getFilledPosition(job, jobStats),
      icon: (
        <TwoPersonIcon size={MOBILE_STAT_ICON_SIZE} color="var(--silver)" />
      ),
    },
    {
      label: 'New Applicants',
      stage: CandidateStage.NEW,
      value: jobStats.countNewApplicants,
      icon: (
        <NotificationBellIcon
          size={MOBILE_STAT_ICON_SIZE}
          color="var(--silver)"
          dotColor={
            jobStats.countNewApplicants > 0 ? 'var(--yellow)' : undefined
          }
        />
      ),
    },
    {
      label: 'All Applicants',
      stage: CandidateStage.APPLICANTS,
      value: getTotalApplicants(jobStats),
      icon: (
        <NotificationBellIcon
          size={MOBILE_STAT_ICON_SIZE}
          color="var(--silver)"
        />
      ),
    },
    {
      label: 'Considering',
      stage: CandidateStage.CONSIDERING,
      value: jobStats.countConsideredWorkers,
      icon: (
        <ClipboardIcon size={MOBILE_STAT_ICON_SIZE} color="var(--silver)" />
      ),
    },
    {
      label: 'Suggested',
      stage: CandidateStage.SUGGESTED,
      value: jobStats.countSuggestedWorkers,
      icon: <DiamondIcon size={MOBILE_STAT_ICON_SIZE} color="var(--silver)" />,
    },
    {
      label: 'Hidden',
      stage: CandidateStage.HIDDEN,
      value: getTotalHiddenApplicants(jobStats),
      icon: (
        <ViewOffSlashIcon size={MOBILE_STAT_ICON_SIZE} color="var(--silver)" />
      ),
    },
  ];

  return (
    <Paper p={8} withBorder radius="md">
      <Stack gap={6}>
        <Group align="flex-start" justify="space-between" wrap="nowrap" gap={4}>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Anchor
              component={Link}
              to={`${BASE_PATH}jobs/${id}`}
              target="_blank"
              fw={700}
              fz={14}
              lh={1.2}
              tt="capitalize"
              c="inherit"
              underline="never"
            >
              {eventName}
              {!datePublished && (
                <Badge variant="draft" tt="none" ml={6} size="sm">
                  Draft
                </Badge>
              )}
            </Anchor>
            <Text fz={12} lh={1.3}>
              {position} at{' '}
              <Text span fw={700} fz={12}>
                {brandRepresented}
              </Text>
            </Text>
          </Stack>

          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="transparent"
                size="sm"
                data-testid="job-actions-menu"
              >
                <SlOptionsVertical size={16} color="var(--black)" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<HiOutlineShare size={LARGE_ICONS_SIZE} />}
                onClick={() => handleOpenModalShareJobLink(job)}
                data-testid="share-job-action"
              >
                Share Job
              </Menu.Item>
              {isJobHidden(job, companyMemberId) ? (
                <Menu.Item
                  leftSection={<FiEye size={LARGE_ICONS_SIZE} />}
                  onClick={() => hideJob(job)}
                  data-testid="unhide-job-action"
                >
                  Unhide Job From My List
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<FiEyeOff size={LARGE_ICONS_SIZE} />}
                  onClick={() => hideJob(job)}
                  data-testid="hide-job-action"
                >
                  Hide Job From My List
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<PiPulseBold size={LARGE_ICONS_SIZE} />}
                component={Link}
                to={`${BASE_PATH}jobs/${id}?activeTab=activityTracker`}
                target="_blank"
                data-testid="activity-tracker-action"
              >
                Activity Tracker
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {nationalJob ? (
          <Text fz={11} lh={1.3} c="var(--silver)" tt="capitalize">
            National Job
          </Text>
        ) : (
          <Anchor
            href={getGoogleMapsUrl(address?.googleAddress || '')}
            target="_blank"
            rel="noopener noreferrer"
            underline="never"
            c="var(--silver)"
          >
            <Group gap={3} wrap="nowrap" align="flex-start">
              <LuMapPin size={MOBILE_META_ICON_SIZE} />
              <Text span fz={11} lh={1.3} tt="capitalize">
                {address?.googleAddress}
              </Text>
            </Group>
          </Anchor>
        )}

        <Group gap="sm" wrap="nowrap">
          <Group gap={3} wrap="nowrap">
            <LuCalendarDays size={MOBILE_META_ICON_SIZE} />
            <Text span fz={11} lh={1.2}>
              {formatLocalDate(
                localizedStart,
                DATE_FORMATS.DATE_FORMAT_SHORT,
                UTC_TIMEZONE
              )}{' '}
              -{' '}
              {localizedEnd
                ? formatLocalDate(
                    localizedEnd,
                    DATE_FORMATS.DATE_FORMAT_SHORT,
                    UTC_TIMEZONE
                  )
                : 'on going'}
            </Text>
          </Group>
          <UnstyledButton
            data-testid="mobile-internal-members"
            onClick={() =>
              showJobMembers(members.filter((item) => item.isInternal))
            }
          >
            <Group gap={3} wrap="nowrap">
              <GoPerson size={MOBILE_META_ICON_SIZE} />
              <Text span fw={700} td="underline" fz={11} lh={1.2}>
                Internal:{' '}
                {members.filter((item) => item.isInternal).length || 0}
              </Text>
            </Group>
          </UnstyledButton>
        </Group>

        <Divider />

        <Group gap={0} wrap="nowrap" align="stretch">
          {stats.map((stat, index) => (
            <Group key={stat.stage} gap={0} wrap="nowrap" style={{ flex: 1 }}>
              {index > 0 && <Divider orientation="vertical" />}
              <MobileStatIndicator
                label={stat.label}
                value={stat.value}
                icon={stat.icon}
                to={jobDetailTabLink(stat.stage)}
                testId={`mobile-stat-${stat.stage}`}
              />
            </Group>
          ))}
        </Group>
      </Stack>
    </Paper>
  );
}
